import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '@/store/settingsStore';

/**
 * I18nProvider Component
 * 
 * Synchronizes language settings between i18next and the settings store.
 * Ensures language preference is properly initialized and persisted.
 */

interface I18nProviderProps {
  children: React.ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const { i18n } = useTranslation();
  const language = useSettingsStore((state) => state.language);
  const setLanguage = useSettingsStore((state) => state.setLanguage);

  // Initialize language from store on mount
  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, []);

  // Sync store language when i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      if (language !== lng) {
        setLanguage(lng);
      }
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, language, setLanguage]);

  // Sync i18n language when store language changes
  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  return <>{children}</>;
}